import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Checkbox,
  Select,
  Option,
  Sheet,
  Chip,
} from "@mui/joy";
import { X } from "@phosphor-icons/react";
// Use the centralized PDF.js setup
import { pdfjs, ensureWorkerReady } from "../../lib/pdfWorkerSetup";
import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import type { ContractField } from "../../types";
import { fetchApiBlob } from "../../lib/api";
import { SIGNER_TYPES, getSignerColor } from "./contracts/contractFieldStyles";

interface PdfFieldDesignerProps {
  readOnly?: boolean;
  pdfData?: Uint8Array | null;
  pdfUrl?: string;
  contractId: string | number;
  existingFields?: ContractField[];
  selectedFieldType?: string;
  dropdownOptions?: string[];
  onFieldAdded?: (field: ContractField) => void;
  onFieldUpdated?: (field: ContractField) => void;
  onFieldDeleted?: (fieldId: string) => void;
  /** Server id a locally placed field was saved under, if it has been saved yet. */
  resolveFieldId?: (fieldId: string) => string | undefined;
}

// Resize handle positions
type ResizeHandle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

// Default field sizes based on type (reduced by 40%)
const fieldSizes: Record<string, { width: number; height: number }> = {
  signature: { width: 120, height: 36 },
  text: { width: 120, height: 18 },
  date: { width: 90, height: 18 },
  checkbox: { width: 12, height: 12 },
  initial: { width: 36, height: 24 },
  dropdown: { width: 120, height: 18 },
};

export const PdfFieldDesigner: React.FC<PdfFieldDesignerProps> = ({
  readOnly = false,
  pdfData,
  pdfUrl,
  contractId,
  existingFields = [],
  selectedFieldType,
  dropdownOptions,
  onFieldAdded,
  onFieldUpdated,
  onFieldDeleted,
  resolveFieldId,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const renderTaskRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [fields, setFields] = useState<ContractField[]>([]);
  const [selectedField, setSelectedField] = useState<ContractField | null>(
    null,
  );
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<ResizeHandle | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [fieldStart, setFieldStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [mousePosition, setMousePosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [fetchedPdfData, setFetchedPdfData] = useState<Uint8Array | null>(null);
  const hasMovedRef = useRef(false);
  const suppressCanvasClickRef = useRef(false);

  // Fetch PDF from URL if pdfUrl is provided (and pdfData is not)
  useEffect(() => {
    if (pdfUrl && !pdfData) {
      const fetchPdf = async () => {
        try {
          setLoading(true);
          setError(null);
          const blob = await fetchApiBlob(pdfUrl);
          const arrayBuffer = await blob.arrayBuffer();
          setFetchedPdfData(new Uint8Array(arrayBuffer));
        } catch (err) {
          setError(
            `Failed to fetch PDF: ${err instanceof Error ? err.message : String(err)}`,
          );
          setLoading(false);
        }
      };
      fetchPdf();
    }
  }, [pdfUrl, pdfData]);

  // Set existing fields and preserve selection across server ID reconciliation
  useEffect(() => {
    setFields(existingFields);
    setSelectedField((prev) => {
      if (!prev) return null;
      // Match the server id first: a field dragged before its add_field POST returned no
      // longer sits at the saved x/y, so the position fallback below would drop the selection.
      const resolvedId = resolveFieldId?.(prev.id);
      const byId = existingFields.find(
        (f) => f.id === prev.id || (resolvedId !== undefined && f.id === resolvedId),
      );
      if (byId) return byId;
      return (
        existingFields.find(
          (f) =>
            f.page === prev.page &&
            f.type === prev.type &&
            Math.abs(f.x - prev.x) < 0.01 &&
            Math.abs(f.y - prev.y) < 0.01,
        ) || null
      );
    });
  }, [existingFields, resolveFieldId]);

  // Start with the whole page visible; users can zoom in to place small fields.
  useEffect(() => {
    const calculateOptimalScale = async () => {
      if (!pdfDoc || !containerRef.current) return;

      try {
        const page = await pdfDoc.getPage(currentPage);
        const viewport = page.getViewport({ scale: 1.0 });

        // Wait for container to be properly sized (especially important in modals)
        await new Promise((resolve) => setTimeout(resolve, 200));

        if (!containerRef.current) return; // Check again after timeout

        const containerRect = containerRef.current.getBoundingClientRect();
        const optimalScale = Math.min(
          (containerRect.width - 32) / viewport.width,
          (containerRect.height - 32) / viewport.height,
        );
        const boundedScale = Math.min(Math.max(optimalScale, 0.5), 2.0);
        setScale(boundedScale);
      } catch (err) {
        console.warn("Could not calculate optimal scale:", err);
      }
    };

    // Delay initial calculation to ensure modal is fully rendered
    const timeoutId = setTimeout(calculateOptimalScale, 300);

    // Recalculate on window resize
    const handleResize = () => {
      setTimeout(calculateOptimalScale, 200); // Longer debounce for modal
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, [pdfDoc, currentPage]);

  // Use pdfData prop if provided, otherwise use fetched data from pdfUrl
  const effectivePdfData = pdfData || fetchedPdfData;

  useEffect(() => {
    if (effectivePdfData) {
      loadPdf();
    }

    // Cleanup on unmount
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
    };
  }, [effectivePdfData]);

  useEffect(() => {
    if (pdfDoc && currentPage) {
      renderPage();
    }
  }, [pdfDoc, currentPage, scale]);

  const loadPdf = async () => {
    if (!effectivePdfData) return;

    try {
      setLoading(true);
      setError(null);

      // Ensure worker is ready
      await ensureWorkerReady();

      // Load PDF from Uint8Array data directly (CSP-compliant, no blob URLs)
      // Create a fresh copy since PDF.js transfers ownership to the worker
      const dataCopy = new Uint8Array(effectivePdfData);
      const loadingTask = pdfjs.getDocument({
        data: dataCopy,
      });
      const pdf = await loadingTask.promise;

      setPdfDoc(pdf as any);
      setTotalPages(pdf.numPages);
      setCurrentPage(1);
    } catch (err) {
      setError(
        `Failed to load PDF: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const renderPage = async () => {
    if (!pdfDoc || !canvasRef.current || !containerRef.current) return;

    try {
      // Cancel any ongoing render task
      if (renderTaskRef.current) {
        try {
          await renderTaskRef.current.cancel();
        } catch (e) {
          // Ignore cancellation errors
        }
        renderTaskRef.current = null;
      }

      // Get the page
      const page: PDFPageProxy = await pdfDoc.getPage(currentPage);

      // Get viewport
      const viewport = page.getViewport({ scale });

      // Prepare canvas
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      if (!context) return;

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Render PDF page into canvas context
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
        canvas: canvas,
      };

      const renderTask = page.render(renderContext);
      renderTaskRef.current = renderTask;

      await renderTask.promise;

      // Update overlay dimensions to match canvas
      if (overlayRef.current) {
        overlayRef.current.style.width = `${canvas.width}px`;
        overlayRef.current.style.height = `${canvas.height}px`;
      }
    } catch (err) {
      if ((err as any)?.name !== "RenderingCancelledException") {
        setError(
          `Failed to render page: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    }
  };

  const handleCanvasClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    if (suppressCanvasClickRef.current) {
      suppressCanvasClickRef.current = false;
      return;
    }

    if (readOnly || !overlayRef.current || !pdfDoc) return;

    // Check if we clicked on an existing field
    const target = event.target as HTMLElement;
    if (target.closest("[data-field-id]")) {
      return; // Let the field handle its own click
    }

    if (!selectedFieldType) {
      setSelectedField(null);
      return;
    }

    const rect = overlayRef.current.getBoundingClientRect();
    const x = (event.clientX - rect.left) / scale;
    const y = (event.clientY - rect.top) / scale;

    const size = fieldSizes[selectedFieldType] || { width: 200, height: 30 };

    const newField: ContractField = {
      id: `field_${Date.now()}`,
      type: selectedFieldType as
        | "text"
        | "initial"
        | "date"
        | "signature"
        | "checkbox"
        | "radio"
        | "dropdown",
      page: currentPage - 1,
      x: x,
      y: y,
      width: size.width,
      height: size.height,
      required: true,
      signer: "customer",
      label: `${selectedFieldType} field`,
      // A choice field with no options renders an empty Select the signer cannot
      // satisfy, which blocks completion while it is required.
      ...(selectedFieldType === "dropdown"
        ? { options: dropdownOptions ?? [] }
        : {}),
    };

    // Add to local state and select the newly placed field so its properties are immediately editable
    setFields((prev) => [...prev, newField]);
    setSelectedField(newField);

    // Call the callback
    onFieldAdded?.(newField);
  };

  const getFieldColor = (field: ContractField) => getSignerColor(field.signer);

  const handleFieldMouseDown = (
    event: React.MouseEvent,
    field: ContractField,
    handle?: ResizeHandle,
  ) => {
    if (readOnly) return;
    event.stopPropagation();
    event.preventDefault();

    hasMovedRef.current = false;
    suppressCanvasClickRef.current = true;
    setSelectedField(field);

    // Overlay-relative, so a drag still tracks the content if the container scrolls mid-drag
    const rect = overlayRef.current?.getBoundingClientRect();
    setDragStart({
      x: event.clientX - (rect?.left ?? 0),
      y: event.clientY - (rect?.top ?? 0),
    });
    setFieldStart({
      x: field.x,
      y: field.y,
      width: field.width,
      height: field.height,
    });

    if (handle) {
      setIsResizing(handle);
    } else {
      setIsDragging(true);
    }
  };

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!overlayRef.current || (!isDragging && !isResizing) || !selectedField)
        return;

      const rect = overlayRef.current.getBoundingClientRect();
      const rawDx = event.clientX - rect.left - dragStart.x;
      const rawDy = event.clientY - rect.top - dragStart.y;
      if (!hasMovedRef.current && Math.hypot(rawDx, rawDy) < 3) {
        return;
      }
      hasMovedRef.current = true;

      const dx = rawDx / scale;
      const dy = rawDy / scale;

      if (isDragging) {
        // Update field position
        const updatedField = {
          ...selectedField,
          x: Math.max(0, fieldStart.x + dx),
          y: Math.max(0, fieldStart.y + dy),
        };

        setSelectedField(updatedField);
        setFields((prev) =>
          prev.map((f) => (f.id === selectedField.id ? updatedField : f)),
        );
      } else if (isResizing) {
        // Calculate new dimensions based on resize handle
        let newX = fieldStart.x;
        let newY = fieldStart.y;
        let newWidth = fieldStart.width;
        let newHeight = fieldStart.height;

        switch (isResizing) {
          case "e":
            newWidth = Math.max(20, fieldStart.width + dx);
            break;
          case "w":
            newWidth = Math.max(20, fieldStart.width - dx);
            newX = fieldStart.x + dx;
            break;
          case "n":
            newHeight = Math.max(20, fieldStart.height - dy);
            newY = fieldStart.y + dy;
            break;
          case "s":
            newHeight = Math.max(20, fieldStart.height + dy);
            break;
          case "ne":
            newWidth = Math.max(20, fieldStart.width + dx);
            newHeight = Math.max(20, fieldStart.height - dy);
            newY = fieldStart.y + dy;
            break;
          case "nw":
            newWidth = Math.max(20, fieldStart.width - dx);
            newHeight = Math.max(20, fieldStart.height - dy);
            newX = fieldStart.x + dx;
            newY = fieldStart.y + dy;
            break;
          case "se":
            newWidth = Math.max(20, fieldStart.width + dx);
            newHeight = Math.max(20, fieldStart.height + dy);
            break;
          case "sw":
            newWidth = Math.max(20, fieldStart.width - dx);
            newHeight = Math.max(20, fieldStart.height + dy);
            newX = fieldStart.x + dx;
            break;
        }

        const updatedField = {
          ...selectedField,
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight,
        };

        setSelectedField(updatedField);
        setFields((prev) =>
          prev.map((f) => (f.id === selectedField.id ? updatedField : f)),
        );
      }
    },
    [isDragging, isResizing, selectedField, dragStart, fieldStart, scale],
  );

  const handleMouseUp = useCallback(() => {
    if (selectedField && (isDragging || isResizing) && hasMovedRef.current) {
      // Find the updated field and notify parent
      const updatedField = fields.find((f) => f.id === selectedField.id);
      if (updatedField) {
        setSelectedField(updatedField);
        onFieldUpdated?.(updatedField);
      }
    }

    hasMovedRef.current = false;
    setIsDragging(false);
    setIsResizing(null);
    setTimeout(() => {
      suppressCanvasClickRef.current = false;
    }, 0);
  }, [selectedField, isDragging, isResizing, fields, onFieldUpdated]);

  // Add global mouse event listeners
  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const handleFieldDelete = (fieldId: string) => {
    if (readOnly) return;
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
    onFieldDeleted?.(fieldId);
    setSelectedField(null);
  };

  const handleZoomIn = () => setScale((s) => Math.min(s + 0.1, 2.0));
  const handleFitPage = async () => {
    if (!pdfDoc || !containerRef.current) return;
    const page = await pdfDoc.getPage(currentPage);
    const viewport = page.getViewport({ scale: 1 });
    const bounds = containerRef.current.getBoundingClientRect();
    setScale(Math.max(0.5, Math.min(2, (bounds.width - 32) / viewport.width, (bounds.height - 32) / viewport.height)));
  };
  const handleZoomOut = () => setScale((s) => Math.max(s - 0.1, 0.5));
  const handlePrevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((p) => Math.min(p + 1, totalPages));

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading PDF...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography color="danger">{error}</Typography>
        <Button onClick={loadPdf}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: "grid", position: "relative", width: "100%", height: "100%", minWidth: 0, minHeight: 0,
      gridTemplateRows: "auto minmax(0, 1fr)",
      gridTemplateColumns: { xs: "minmax(0, 1fr)", md: !readOnly ? "minmax(0, 1fr) 280px" : "minmax(0, 1fr)" },
    }}>
      {/* Toolbar */}
      <Box
        sx={{
          gridColumn: 1, gridRow: 1,
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Button size="sm" onClick={handleZoomOut} disabled={scale <= 0.5}>
          Zoom Out
        </Button>
        <Typography level="body-sm">{Math.round(scale * 100)}%</Typography>
        <Button size="sm" onClick={handleZoomIn} disabled={scale >= 2.0}>
          Zoom In
        </Button>
        <Button size="sm" variant="plain" onClick={() => void handleFitPage()}>Fit page</Button>

        <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            size="sm"
            onClick={handlePrevPage}
            disabled={currentPage <= 1}
          >
            Prev
          </Button>
          <Typography level="body-sm">
            Page {currentPage} of {totalPages}
          </Typography>
          <Button
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage >= totalPages}
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* PDF Canvas Container */}
      <Box
        ref={containerRef}
        sx={{
          gridColumn: 1, gridRow: 2, minWidth: 0,
          overflow: "auto",
          bgcolor: "background.level2",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          p: 2,
          minHeight: 0, // Allow container to shrink
        }}
      >
        <Box
          sx={{
            position: "relative",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              display: "block",
              backgroundColor: "white",
            }}
          />

          {/* Field overlay */}
          <Box
            ref={overlayRef}
            onClick={handleCanvasClick}
            onMouseMove={(e) => {
              if (!overlayRef.current || selectedField) {
                setMousePosition(null);
                return;
              }
              const rect = overlayRef.current.getBoundingClientRect();
              setMousePosition({
                x: (e.clientX - rect.left) / scale,
                y: (e.clientY - rect.top) / scale,
              });
            }}
            onMouseLeave={() => setMousePosition(null)}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              cursor:
                selectedField || !selectedFieldType ? "default" : "crosshair",
              userSelect: "none",
            }}
          >
            {fields
              .filter((field) => field.page === currentPage - 1)
              .map((field) => {
                const isSelected = selectedField?.id === field.id;
                const isHovered = hoveredField === field.id;
                const color = getFieldColor(field);

                return (
                  <Box
                    key={field.id}
                    data-field-id={field.id}
                    tabIndex={readOnly ? -1 : 0}
                    role="button"
                    aria-label={`${field.label || field.type}, page ${currentPage}. Arrow keys move; Shift moves ten points.`}
                    onFocus={() => setSelectedField(field)}
                    onKeyDown={(event) => {
                      if (readOnly) return;
                      if (event.key === "Delete") {
                        event.preventDefault();
                        handleFieldDelete(field.id);
                        return;
                      }
                      const delta = event.shiftKey ? 10 : 1;
                      const move: Record<string, [number, number]> = {
                        ArrowLeft: [-delta, 0],
                        ArrowRight: [delta, 0],
                        ArrowUp: [0, -delta],
                        ArrowDown: [0, delta],
                      };
                      if (!move[event.key]) return;
                      event.preventDefault();
                      const [dx, dy] = move[event.key];
                      const updated = {
                        ...field,
                        x: Math.max(
                          0,
                          Math.min(
                            field.x + dx,
                            (canvasRef.current?.width || 0) / scale -
                              field.width,
                          ),
                        ),
                        y: Math.max(
                          0,
                          Math.min(
                            field.y + dy,
                            (canvasRef.current?.height || 0) / scale -
                              field.height,
                          ),
                        ),
                      };
                      setSelectedField(updated);
                      setFields((prev) =>
                        prev.map((f) => (f.id === field.id ? updated : f)),
                      );
                      onFieldUpdated?.(updated);
                    }}
                    onMouseEnter={() => setHoveredField(field.id)}
                    onMouseLeave={() => setHoveredField(null)}
                    onMouseDown={(e) => handleFieldMouseDown(e, field)}
                    onClick={(e) => {
                      e.stopPropagation();
                      suppressCanvasClickRef.current = false;
                      setSelectedField(field);
                    }}
                    sx={{
                      position: "absolute",
                      left: `${field.x * scale}px`,
                      top: `${field.y * scale}px`,
                      width: `${field.width * scale}px`,
                      height: `${field.height * scale}px`,
                      border: `2px ${isSelected ? "solid" : "dashed"} ${color}`,
                      backgroundColor:
                        isSelected || isHovered ? `${color}20` : "transparent",
                      cursor: isDragging ? "grabbing" : "grab",
                      transition:
                        isSelected || isDragging || isResizing
                          ? "none"
                          : "all 0.1s ease",
                    }}
                  >
                    {/* Field label */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: "-24px",
                        left: "-2px",
                        backgroundColor: color,
                        color: "white",
                        fontSize: "11px",
                        padding: "2px 6px",
                        borderRadius: "3px 3px 0 0",
                        whiteSpace: "nowrap",
                        pointerEvents: "none",
                      }}
                    >
                      {field.type.toUpperCase()} -{" "}
                      {SIGNER_TYPES.find((s) => s.id === field.signer)?.label ||
                        "Unknown"}
                    </Box>

                    {/* Delete button */}
                    {isSelected && !readOnly && (
                      <IconButton
                        size="sm"
                        variant="soft"
                        color="danger"
                        aria-label={`Remove ${field.label || field.type + " field"}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFieldDelete(field.id);
                        }}
                        sx={{
                          position: "absolute",
                          top: "-8px",
                          right: "-8px",
                          minWidth: "24px",
                          width: "24px",
                          height: "24px",
                        }}
                      >
                        <X size={14} />
                      </IconButton>
                    )}

                    {/* Resize handles */}
                    {isSelected && !readOnly && (
                      <>
                        {(
                          [
                            "n",
                            "s",
                            "e",
                            "w",
                            "ne",
                            "nw",
                            "se",
                            "sw",
                          ] as ResizeHandle[]
                        ).map((handle) => (
                          <Box
                            key={handle}
                            onMouseDown={(e) =>
                              handleFieldMouseDown(e, field, handle)
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              suppressCanvasClickRef.current = false;
                            }}
                            sx={{
                              position: "absolute",
                              width: "8px",
                              height: "8px",
                              backgroundColor: color,
                              border: "1px solid white",
                              ...(handle.includes("n") && { top: "-4px" }),
                              ...(handle.includes("s") && { bottom: "-4px" }),
                              ...(handle.includes("e") && { right: "-4px" }),
                              ...(handle.includes("w") && { left: "-4px" }),
                              ...(handle === "n" || handle === "s"
                                ? { left: "50%", transform: "translateX(-50%)" }
                                : {}),
                              ...(handle === "e" || handle === "w"
                                ? { top: "50%", transform: "translateY(-50%)" }
                                : {}),
                              cursor: `${handle}-resize`,
                            }}
                          />
                        ))}
                      </>
                    )}
                  </Box>
                );
              })}

            {/* Ghost field preview when hovering */}
            {!readOnly &&
              mousePosition &&
              !selectedField &&
              selectedFieldType && (
                <Box
                  sx={{
                    position: "absolute",
                    left: `${mousePosition.x * scale}px`,
                    top: `${mousePosition.y * scale}px`,
                    width: `${(fieldSizes[selectedFieldType]?.width || 200) * scale}px`,
                    height: `${(fieldSizes[selectedFieldType]?.height || 30) * scale}px`,
                    border: "2px dashed #1976d2",
                    backgroundColor: "rgba(25, 118, 210, 0.1)",
                    pointerEvents: "none",
                    opacity: 0.5,
                  }}
                />
              )}
          </Box>
        </Box>
      </Box>

      {/* Field properties inspector rail */}
      {!readOnly && (
        <Sheet
          component="aside"
          aria-label="Field properties"
          sx={{
            gridColumn: 2,
            gridRow: "1 / span 2",
            display: { xs: selectedField ? "block" : "none", md: "block" },
            position: { xs: "absolute", md: "relative" },
            top: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            width: { xs: "min(280px, 90%)", md: "auto" },
            overflowY: "auto",
            minHeight: 0,
            p: 2,
            borderLeft: "1px solid",
            borderColor: "divider",
            boxShadow: { xs: "lg", md: "none" },
          }}
        >
          {selectedField ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography level="title-md">Field Properties</Typography>
                <IconButton size="sm" aria-label="Close field properties" onClick={() => setSelectedField(null)}><X /></IconButton>
              </Box>
              <Typography level="body-sm" sx={{ mb: 1 }}>Align on page</Typography>
              <Box sx={{ display: "flex", gap: 0.5, mb: 2 }}>
                {["Left", "Center", "Right"].map((alignment) => (
                  <Button
                    key={alignment}
                    variant="outlined"
                    color="neutral"
                    sx={{ flex: 1, minWidth: 0 }}
                    size="sm"
                    onClick={() => {
                      const width = (canvasRef.current?.width || 0) / scale;
                      const updated = {
                        ...selectedField,
                        x:
                          alignment === "Left"
                            ? 0
                            : alignment === "Center"
                              ? (width - selectedField.width) / 2
                              : width - selectedField.width,
                      };
                      setSelectedField(updated);
                      setFields((prev) =>
                        prev.map((f) => (f.id === updated.id ? updated : f)),
                      );
                      onFieldUpdated?.(updated);
                    }}
                  >
                    {alignment}
                  </Button>
                ))}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Typography level="body-sm" sx={{ mb: 0.5 }}>
                    Type
                  </Typography>
                  <Chip size="sm" color="primary">
                    {selectedField.type}
                  </Chip>
                </Box>

                <Box>
                  <Typography level="body-sm" sx={{ mb: 0.5 }}>
                    Signer
                  </Typography>
                  <Select
                    size="sm"
                    value={selectedField.signer}
                    onChange={(_, value) => {
                      if (value) {
                        const updatedField = { ...selectedField, signer: value };
                        setFields((prev) =>
                          prev.map((f) =>
                            f.id === selectedField.id ? updatedField : f,
                          ),
                        );
                        setSelectedField(updatedField);
                        onFieldUpdated?.(updatedField);
                      }
                    }}
                  >
                    {SIGNER_TYPES.map((signer) => (
                      <Option key={signer.id} value={signer.id}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor: signer.color,
                            }}
                          />
                          {signer.label}
                        </Box>
                      </Option>
                    ))}
                  </Select>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    size="sm"
                    checked={selectedField.required || false}
                    onChange={(e) => {
                      const updatedField = {
                        ...selectedField,
                        required: e.target.checked,
                      };
                      setFields((prev) =>
                        prev.map((f) =>
                          f.id === selectedField.id ? updatedField : f,
                        ),
                      );
                      setSelectedField(updatedField);
                      onFieldUpdated?.(updatedField);
                    }}
                  />
                  <Typography level="body-sm">Required field</Typography>
                </Box>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 1,
                color: "text.tertiary",
              }}
            >
              <Typography level="title-sm" sx={{ mb: 0.5, color: "text.secondary" }}>
                No Field Selected
              </Typography>
              <Typography level="body-xs">
                Click a placed field on the PDF to configure its signer, alignment, and requirements.
              </Typography>
            </Box>
          )}
        </Sheet>
      )}
    </Box>
  );
};
